import { GridRowModel } from '@mui/x-data-grid-pro';
import {
	SkuItem,
	InventoryItem,
	FixedFieldValueOption,
	InventoryStatus,
	BaseInventoryItem,
	FixedFieldValues,
	SingleInventoryItem,
	BulkInventoryItem,
	AllocationOption,
} from 'common/modules/inventory';
import { SingleItemFormValues } from './SingleItem/types';
import { Location, ProductApi, PurchaseTypes } from 'common/types/common';
import { TFunction } from 'i18next';
import { Api } from 'common/db/api/paths';
import { FieldValues } from 'common/frontend/firebase/firestore';
import { InventoryConfiguration, InventoryField } from 'common/modules/inventory';
import { hashByUniqueField } from 'common/utils/arrays';
import { sum } from 'lodash';

export interface InventoryRowItemParams {
	inventoryItems: InventoryItem[];
	storeLocations: Location[];
	selectedLocationIds?: string[];
	statuses?: InventoryStatus[];
	skuIds?: string[];
	t: TFunction;
}

export interface SkuRowItemParams {
	skuItems: SkuItem[];
	stockProducts: ProductApi[];
	selectedLocationIds?: string[];
	t: TFunction;
}

export const saveNewInventoryFieldsToDb = (
	api: Api,
	inventoryFields: InventoryField[],
	shopId: string,
) => {
	if (!inventoryFields.length) return;
	const configurationDocWithoutFields: Omit<InventoryConfiguration, 'fields'> = {
		id: shopId,
		shopId,
	};
	// We use set with merge, because the document might not exist yet
	const inventoryFieldObject = hashByUniqueField(inventoryFields, 'id');
	return api.inventoryConfigurations.doc(shopId).set(
		{
			...configurationDocWithoutFields,
			fields: {
				order: FieldValues.arrayUnion(...inventoryFields.map((f) => f.id)),
				values: {
					...inventoryFieldObject,
				},
			},
		},
		{ merge: true },
	);
};

export interface SkuOption {
	id: string;
	label: string;
	type: SkuItem['itemType'];
}

export const skuItemsToOptions = (skuItems: SkuItem[]): SkuOption[] => {
	const sortSkuOptions = (opt1: SkuOption, opt2: SkuOption) => {
		return opt1.label.localeCompare(opt2.label);
	};
	return skuItems
		.map((s) => ({ id: s.id, label: `${s.skuName} (${s.skuCode})`, type: s.itemType }))
		.sort(sortSkuOptions);
};

export const localizedType = (type: InventoryItem['type'], t: TFunction) => {
	const locales: Record<InventoryItem['type'], string> = {
		single: t('inventory.itemType', 'Individual'),
		bulk: t('inventory.bulkType', 'Bulk'),
	};
	return locales[type] ?? type;
};

export const getFilteredRowsBySearchValue = (rows: GridRowModel[], searchValue: string) => {
	if (searchValue === '') return rows;
	const escapeRegExp = (value: string): string => {
		return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
	};
	const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
	return rows.filter((row) => {
		return Object.values(row).some((value) => {
			if (value == null) return false;
			return searchRegex.test(value.toString());
		});
	});
};

export const getFixedFieldValueTranslation = (
	t: TFunction,
	fixedValueOption: FixedFieldValueOption | undefined,
) => {
	if (!fixedValueOption) return '';
	const fixedFieldValueTranslations: Record<FixedFieldValueOption, string> = {
		soleLength: t('inventory.fixedFieldValues.soleLength', 'Sole length'),
		dinVariation: t('inventory.fixedFieldValues.dinVariation', 'DIN variation'),
	};
	return fixedFieldValueTranslations[fixedValueOption] ?? fixedValueOption;
};

export const getInventoryStatusTranslation = (t: TFunction, status: InventoryStatus) => {
	const translations: Record<InventoryStatus, string> = {
		IN_USE: t('inventory.inUse', 'In use'),
		OUT_OF_USE: t('inventory.outOfUse', 'Out of use'),
		SOLD: t('inventory.sold', 'Sold'),
		LOST: t('inventory.lost', 'Lost'),
	};
	return translations[status] ?? status;
};

export const getInventoryStateTranslation = (state: 'IN' | 'OUT') => {
	const translations: Record<'IN' | 'OUT', string> = {
		IN: 'In',
		OUT: 'Out',
	};
	return translations[state] ?? state;
};

export const getInventoryAllocationOptionTranslation = (
	t: TFunction,
	allocationOption: AllocationOption,
) => {
	const translations: Record<AllocationOption, string> = {
		rental: t('inventory.allocation.rental', 'Rental'),
		sales: t('inventory.allocation.sales', 'Sales'),
	};
	return translations[allocationOption] ?? allocationOption;
};

export const nameToSkuCode = (name: string, existingSkus: string[]): string => {
	const trimmedUppercaseName = name.trim().toUpperCase();
	// Replace all non-alphanumeric characters with a single space
	const formattedName = trimmedUppercaseName.replace(/[\W_]+/g, ' ');
	const nameStrings = formattedName.split(' ').slice(0, 3);
	const charsToTake = nameStrings.length === 1 ? 3 : 2;
	const idPrefix = nameStrings.map((string) => string.substring(0, charsToTake)).join('-');
	let numberSuffix = 0;
	let sku = '';
	do {
		if (numberSuffix === 0) {
			sku = idPrefix;
		} else {
			sku = `${idPrefix}-${numberSuffix}`;
		}
		numberSuffix++;
	} while (existingSkus.includes(sku));
	return sku;
};

interface NewSkuItemProps<T extends SkuItem['itemType']> {
	id: string;
	name: string;
	skuCode: string;
	shopId: string;
	itemType: T;
}

export const getNewSkuItem = <T extends SkuItem['itemType']>(
	props: NewSkuItemProps<T>,
): SkuItem => {
	const { id, shopId, name, skuCode, itemType } = props;
	const skuItem: SkuItem = {
		id,
		createdAt: new Date().toISOString(),
		shopId,
		skuName: name,
		skuCode,
		quantity: 0,
		usageCount: 0,
		usageSeconds: 0,
		lastUsed: null,
		statusesByLocation: {},
		allocationsByLocation: {},
		itemType,
	};
	return skuItem;
};

interface NewBaseInventoryItemProps {
	id: string;
	skuName: string;
	skuCode: string;
	skuId: string;
	shopId: string;
	locationId: string;
	identifier: string[] | null;
	fieldValues: {
		[fieldId: string]: string | number;
	};
	fixedFieldValues: FixedFieldValues | undefined;
}

interface NewSingleInventoryItemProps extends NewBaseInventoryItemProps {
	type: 'single';
	status: SingleInventoryItem['status'];
	allocation: SingleInventoryItem['allocation'];
}

interface NewBulkInventoryItemProps extends NewBaseInventoryItemProps {
	type: 'bulk';
	quantity: number;
	statuses: BulkInventoryItem['statuses'];
	allocations: BulkInventoryItem['allocations'];
}

export function getNewInventoryItem(props: NewSingleInventoryItemProps): SingleInventoryItem;
export function getNewInventoryItem(props: NewBulkInventoryItemProps): BulkInventoryItem;
export function getNewInventoryItem(
	props: NewSingleInventoryItemProps | NewBulkInventoryItemProps,
): SingleInventoryItem | BulkInventoryItem {
	const {
		id,
		shopId,
		identifier,
		locationId,
		skuId,
		skuCode,
		fieldValues,
		skuName,
		fixedFieldValues,
	} = props;
	const baseInventoryItem: BaseInventoryItem = {
		id,
		skuName,
		createdAt: new Date().toISOString(),
		shopId,
		identifiers: !!identifier ? identifier : [],
		usageCount: 0,
		usageSeconds: 0,
		lastUsed: null,
		locationId,
		skuId,
		skuCode,
		fieldValues,
		activeOrderIds: [],
		...(fixedFieldValues && { fixedFieldValues }),
	};
	if (props.type === 'single') {
		return {
			...baseInventoryItem,
			type: 'single',
			status: props.status,
			allocation: props.allocation,
		};
	}
	return {
		...baseInventoryItem,
		type: 'bulk',
		quantity: props.quantity,
		statuses: props.statuses,
		allocations: props.allocations,
	};
}

export const usageHoursToFormattedTime = (hours: number): string => {
	const formattedHours = parseFloat(hours.toFixed(1));
	return `${formattedHours} h`;
};

export const usageDaysToFormattedTime = (days: number): string => {
	const formattedHours = parseFloat(days.toFixed(1));
	return `${formattedHours} d`;
};

export const allocationOptionValues = Object.values(PurchaseTypes);

export const allocationsObjectToSelection = (
	allocation?: SingleInventoryItem['allocation'],
): SingleItemFormValues['allocation'] => {
	if (!allocation) return 'rental';

	const enabledAllocationsArray = Object.entries(allocation)
		.filter(([_k, v]) => v)
		.map(([k, _v]) => k);

	const isRentable = enabledAllocationsArray.includes(PurchaseTypes.rental);
	const isSellable = enabledAllocationsArray.includes(PurchaseTypes.sales);
	const defaultValue = PurchaseTypes.rental;

	return isRentable ? PurchaseTypes.rental : isSellable ? PurchaseTypes.sales : defaultValue;
};

export const allocationSelectionToObject = (
	selection?: SingleItemFormValues['allocation'],
): SingleInventoryItem['allocation'] => {
	switch (selection) {
		case PurchaseTypes.rental:
			return { rental: true };
		case PurchaseTypes.sales:
			return { sales: true };
		default:
			return { rental: true };
	}
};

export const getSkuInStockAmountByLocation = (skuItem: SkuItem | undefined, locationId: string) => {
	if (!skuItem) {
		return 0;
	}
	return skuItem.statusesByLocation[locationId]?.IN_USE ?? 0;
};

export const getSkuInStockAmountForLocations = (
	skuItem: SkuItem | undefined,
	_locationIds?: string[],
) => {
	const locationIds = _locationIds ?? Object.keys(skuItem?.statusesByLocation ?? {});
	return sum(locationIds.map((locationId) => getSkuInStockAmountByLocation(skuItem, locationId)));
};

export const getSkuAllocationsByLocation = (
	skuItem: SkuItem | undefined,
	locationId: string,
	allocationOption: AllocationOption,
) => {
	if (!skuItem) {
		return 0;
	}
	return skuItem.allocationsByLocation?.[locationId]?.[allocationOption] ?? 0;
};

export const getSkuAllocationsForLocations = (
	skuItem: SkuItem | undefined,
	allocationOption: AllocationOption,
	_locationIds?: string[],
) => {
	const locationIds = _locationIds ?? Object.keys(skuItem?.statusesByLocation ?? {});
	return sum(
		locationIds.map((locationId) =>
			getSkuAllocationsByLocation(skuItem, locationId, allocationOption),
		),
	);
};

export const getSkuReservedAmountByLocation = (
	skuItem: SkuItem | undefined,
	locationId: string,
) => {
	if (!skuItem) {
		return 0;
	}
	return skuItem.reservedOrderCountsByLocation?.[locationId] ?? 0;
};

export const getSkuReservedAmountForLocations = (
	skuItem: SkuItem | undefined,
	_locationIds?: string[],
) => {
	const locationIds = _locationIds ?? Object.keys(skuItem?.statusesByLocation ?? {});
	return sum(locationIds.map((locationId) => getSkuReservedAmountByLocation(skuItem, locationId)));
};
